import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import { default as Select } from 'antd/es/select';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import {
  BUSINESS_UNIT_PLACEHOLDER,
  ENDPOINT_URL_PLACEHOLDER,
  X_AUTH_PLACEHOLDER
} from './AddWebhookModal/constants';
import { AuthKeyOption } from './AuthKeyOption';
import { BusinessUnitOption } from './BusinessUnitOption';
import {
  BULK_EDIT_WEBHOOKS_BUSINESS_UNIT_OPTION_TEST_ID,
  BULK_EDIT_WEBHOOKS_BUSINESS_UNIT_TEST_ID,
  BULK_EDIT_WEBHOOKS_CANCEL_TEST_ID,
  BULK_EDIT_WEBHOOKS_ENDPOINT_ERROR_TEST_ID,
  BULK_EDIT_WEBHOOKS_ENDPOINT_TEST_ID,
  BULK_EDIT_WEBHOOKS_FORM_ID,
  BULK_EDIT_WEBHOOKS_INFO_TEST_ID,
  BULK_EDIT_WEBHOOKS_MODAL_TEST_ID,
  BULK_EDIT_WEBHOOKS_SAVE_TEST_ID,
  BULK_EDIT_WEBHOOKS_TITLE,
  BULK_EDIT_WEBHOOKS_X_AUTH_OPTION_TEST_ID,
  BULK_EDIT_WEBHOOKS_X_AUTH_TEST_ID
} from './constants';
import { FieldErrorLine } from './FieldErrorLine';
import { useBulkEditWebhooksForm } from './useBulkEditWebhooksForm';
import { bulkEditInfoMessage } from './utils';

type BulkEditWebhooksModalProps = {
  uuids: string[];
  open: boolean;
  onCancel: () => void;
  onSaved: () => void;
};

const MODAL_WIDTH = 480;

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 text-xs font-medium text-gray-500">{children}</label>
);

export const BulkEditWebhooksModal = ({
  uuids,
  open,
  onCancel,
  onSaved
}: BulkEditWebhooksModalProps) => {
  const { form, businessUnits, authKeys, hasAnyValue, submit, isSaving } =
    useBulkEditWebhooksForm({ uuids, onSaved });
  const {
    control,
    watch,
    formState: { errors, isValid }
  } = form;

  const endpointUrl = watch('endpointUrl') ?? '';
  const endpointError =
    endpointUrl.length > 0 ? errors.endpointUrl?.message : undefined;

  const cancel = () => {
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={cancel}
      afterClose={() => form.reset()}
      width={MODAL_WIDTH}
      destroyOnHidden
      title={
        <div className="text-xl font-medium">{BULK_EDIT_WEBHOOKS_TITLE}</div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={BULK_EDIT_WEBHOOKS_CANCEL_TEST_ID}
            onClick={cancel}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            data-testid={BULK_EDIT_WEBHOOKS_SAVE_TEST_ID}
            form={BULK_EDIT_WEBHOOKS_FORM_ID}
            htmlType="submit"
            disabled={!hasAnyValue || !isValid || isSaving}
            loading={isSaving}
          >
            Save changes
          </PrimaryButton>
        </div>
      }
    >
      <form
        id={BULK_EDIT_WEBHOOKS_FORM_ID}
        onSubmit={submit}
        data-testid={BULK_EDIT_WEBHOOKS_MODAL_TEST_ID}
        className="flex flex-col gap-4"
      >
        <p
          data-testid={BULK_EDIT_WEBHOOKS_INFO_TEST_ID}
          className="rounded-2xl bg-surface-muted p-3 text-sm text-content-primary"
        >
          {bulkEditInfoMessage(uuids.length)}
        </p>

        <div className="flex flex-col">
          <FieldLabel>Endpoint URL</FieldLabel>
          <Controller
            name="endpointUrl"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid={BULK_EDIT_WEBHOOKS_ENDPOINT_TEST_ID}
                placeholder={ENDPOINT_URL_PLACEHOLDER}
                status={endpointError ? 'error' : undefined}
                className="h-12 rounded-2xl"
              />
            )}
          />
          <FieldErrorLine
            message={endpointError}
            data-testid={BULK_EDIT_WEBHOOKS_ENDPOINT_ERROR_TEST_ID}
          />
        </div>

        <div className="flex flex-col">
          <FieldLabel>Business unit</FieldLabel>
          <Controller
            name="businessUnitUuid"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                data-testid={BULK_EDIT_WEBHOOKS_BUSINESS_UNIT_TEST_ID}
                placeholder={BUSINESS_UNIT_PLACEHOLDER}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={businessUnits.map((unit) => ({
                  value: unit.uuid,
                  label: unit.name
                }))}
                optionRender={(option) => {
                  const unit = businessUnits.find(
                    (item) => item.uuid === option.value
                  );
                  return unit ? (
                    <div
                      data-testid={BULK_EDIT_WEBHOOKS_BUSINESS_UNIT_OPTION_TEST_ID(
                        unit.uuid
                      )}
                    >
                      <BusinessUnitOption {...unit} />
                    </div>
                  ) : null;
                }}
                labelRender={(label) => label.label}
                className="w-full"
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          <FieldLabel>X-Auth-UUID</FieldLabel>
          <Controller
            name="xAuthUuid"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                data-testid={BULK_EDIT_WEBHOOKS_X_AUTH_TEST_ID}
                placeholder={X_AUTH_PLACEHOLDER}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                filterOption={(input, option) =>
                  `${option?.label ?? ''} ${option?.value ?? ''}`
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={authKeys.map((key) => ({
                  value: key.uuid,
                  label: key.name,
                  disabled: !key.enabled
                }))}
                optionRender={(option) => {
                  const key = authKeys.find(
                    (item) => item.uuid === option.value
                  );
                  return key ? (
                    <div
                      data-testid={BULK_EDIT_WEBHOOKS_X_AUTH_OPTION_TEST_ID(
                        key.uuid
                      )}
                    >
                      <AuthKeyOption {...key} />
                    </div>
                  ) : null;
                }}
                labelRender={(label) => label.label}
                className="w-full"
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};
